import React, { useState, useEffect } from 'react';
import { X, Phone, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhoneVerificationModal = ({ open, onClose, email, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (open && email) {
      sendVerificationCode();
    }
  }, [open, email]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendVerificationCode = async () => {
    try {
      const response = await axios.post(`${API}/auth/send-verification-code?email=${encodeURIComponent(email)}`);
      
      // FOR TESTING: Show code in toast (remove in production)
      if (response.data.code) {
        toast.success(`📱 Codice inviato! (Test: ${response.data.code})`, {
          duration: 10000,
          description: 'In produzione, il codice sarà inviato via SMS'
        });
        setSentCode(response.data.code);
      }
      
      setCountdown(60); // 60 seconds before resend
    } catch (error) {
      console.error('Error sending code:', error);
      toast.error('Errore nell\'invio del codice');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-phone-code?email=${encodeURIComponent(email)}&code=${code}`);
      
      // Save token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      toast.success(' Telefono verificato con successo!');
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error verifying code:', error);
      const errorMsg = error.response?.data?.detail || 'Codice non valido';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-slate-900">Verifica Telefono</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-900 mb-2">
                📱 Abbiamo inviato un codice di verifica a 6 cifre al tuo numero di telefono.
              </p>
              <p className="text-xs text-blue-700">
                Inserisci il codice qui sotto per completare la registrazione.
              </p>
            </div>

            {/* FOR TESTING: Show code directly */}
            {sentCode && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-xs text-amber-800 mb-1 font-semibold">
                  🧪 MODALITÀ TEST
                </p>
                <p className="text-sm text-amber-900">
                  Il tuo codice è: <span className="font-mono font-bold text-lg">{sentCode}</span>
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  (In produzione, questo sarà inviato via SMS)
                </p>
              </div>
            )}
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Codice di Verifica
              </label>
              <Input
                type="text"
                maxLength="6"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-2xl font-mono tracking-widest"
                required
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={sendVerificationCode}
                disabled={countdown > 0}
                className="flex-1"
              >
                {countdown > 0 ? `Reinvia (${countdown}s)` : 'Reinvia Codice'}
              </Button>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                {loading ? 'Verifica...' : 'Verifica'}
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p>
                Il codice scade dopo 5 minuti. Non condividere il codice con nessuno.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
